import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Download,
  FileDownload,
  Delete,
  Refresh,
  Settings,
  Schedule,
  CheckCircle,
  Error,
  Pending,
  Info,
  FilterList,
  Clear,
  PlayArrow,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';

interface ExportJob {
  id: string;
  type: 'users' | 'bots' | 'trades' | 'signals' | 'audit_logs';
  format: 'csv' | 'json' | 'excel';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  totalRecords: number;
  processedRecords: number;
  createdAt: string;
  completedAt?: string;
  downloadUrl?: string;
  expiresAt?: string;
  filters: any;
  fileSize?: number;
  error?: string;
}

interface ExportConfig {
  type: string;
  format: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  filters: any;
  fields: string[];
  includeDeleted: boolean;
}

const Export: React.FC = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  
  // State management
  const [config, setConfig] = useState<ExportConfig>({
    type: 'users',
    format: 'csv',
    dateRange: {
      start: null,
      end: null,
    },
    filters: {},
    fields: [],
    includeDeleted: false,
  });
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ExportJob | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Available fields for each data type
  const availableFields = {
    users: ['id', 'email', 'firstName', 'lastName', 'isActive', 'isAdmin', 'createdAt', 'lastLoginAt', 'subscriptionTier'],
    bots: ['id', 'name', 'userId', 'exchange', 'symbol', 'strategy', 'status', 'totalTrades', 'totalProfit', 'createdAt'],
    trades: ['id', 'botId', 'userId', 'symbol', 'side', 'amount', 'price', 'fee', 'status', 'executedAt'],
    signals: ['id', 'symbol', 'action', 'confidence', 'price', 'source', 'processed', 'createdAt'],
    audit_logs: ['id', 'userId', 'action', 'resource', 'details', 'ipAddress', 'userAgent', 'createdAt'],
  };

  // Fetch export jobs
  const { data: jobsData, isLoading, error, refetch } = useQuery({
    queryKey: ['exportJobs'],
    queryFn: () => api.get('/export/jobs'),
    refetchInterval: 5000, // Refresh every 5 seconds to update progress
  });

  const jobs = jobsData?.jobs || [];

  // Create export job mutation
  const createExportMutation = useMutation({
    mutationFn: (exportConfig: ExportConfig) => api.post('/export', exportConfig),
    onSuccess: () => {
      queryClient.invalidateQueries(['exportJobs']);
      setSnackbar({ open: true, message: 'Export job created successfully', severity: 'success' });
      setConfigDialogOpen(false);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to create export job', severity: 'error' });
    },
  });

  // Delete export job mutation
  const deleteJobMutation = useMutation({
    mutationFn: (jobId: string) => api.delete(`/export/jobs/${jobId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['exportJobs']);
      setSnackbar({ open: true, message: 'Export job deleted successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete export job', severity: 'error' });
    },
  });

  // Event handlers
  const handleCreateExport = () => {
    createExportMutation.mutate(config);
  };

  const handleJobDetails = (job: ExportJob) => {
    setSelectedJob(job);
    setJobDetailsOpen(true);
  };

  const handleDownload = (job: ExportJob) => {
    if (job.downloadUrl) {
      window.open(job.downloadUrl, '_blank');
    }
  };

  const handleFieldToggle = (field: string) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.includes(field)
        ? prev.fields.filter(f => f !== field)
        : [...prev.fields, field]
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'processing': return <Schedule />;
      case 'pending': return <Pending />;
      case 'failed': return <Error />;
      default: return <Info />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert error={error} />;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Data Export System
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Export system data in various formats with advanced filtering
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => setConfigDialogOpen(true)}
              size="small"
            >
              New Export
            </Button>
          </Box>
        </Box>

        {/* Export Statistics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {jobs.filter((job: ExportJob) => job.status === 'pending').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {jobs.filter((job: ExportJob) => job.status === 'processing').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Processing Jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {jobs.filter((job: ExportJob) => job.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {jobs.filter((job: ExportJob) => job.status === 'failed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed Jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Export Jobs Table */}
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Export Jobs</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>File Size</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job: ExportJob) => (
                  <TableRow key={job.id} hover>
                    <TableCell>
                      <Chip
                        label={job.type.replace('_', ' ').toUpperCase()}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.format.toUpperCase()}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        color={getStatusColor(job.status) as any}
                        size="small"
                        icon={getStatusIcon(job.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: '100%' }}>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress}
                          color={job.status === 'failed' ? 'error' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {job.progress.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.processedRecords.toLocaleString()} / {job.totalRecords.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.fileSize ? formatFileSize(job.fileSize) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(job.createdAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.expiresAt
                          ? new Date(job.expiresAt).toLocaleDateString()
                          : '-'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleJobDetails(job)}
                          >
                            <Info />
                          </IconButton>
                        </Tooltip>

                        {job.status === 'completed' && job.downloadUrl && (
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownload(job)}
                            >
                              <FileDownload />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this export job?')) {
                                deleteJobMutation.mutate(job.id);
                              }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {jobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary">
                        No export jobs found. Create your first export to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Export Configuration Dialog */}
        <Dialog
          open={configDialogOpen}
          onClose={() => setConfigDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Export</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Data Type</InputLabel>
                  <Select
                    value={config.type}
                    label="Data Type"
                    onChange={(e) => setConfig(prev => ({ ...prev, type: e.target.value, fields: [] }))}
                  >
                    <MenuItem value="users">Users</MenuItem>
                    <MenuItem value="bots">Bots</MenuItem>
                    <MenuItem value="trades">Trades</MenuItem>
                    <MenuItem value="signals">Signals</MenuItem>
                    <MenuItem value="audit_logs">Audit Logs</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={config.format}
                    label="Format"
                    onChange={(e) => setConfig(prev => ({ ...prev, format: e.target.value }))}
                  >
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="excel">Excel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <MUIDatePicker
                  label="Start Date"
                  value={config.dateRange.start}
                  onChange={(date: any) => setConfig(prev => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, start: date }
                  }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MUIDatePicker
                  label="End Date"
                  value={config.dateRange.end}
                  onChange={(date: any) => setConfig(prev => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, end: date }
                  }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Fields to Include
                </Typography>
                <FormGroup row>
                  {availableFields[config.type as keyof typeof availableFields]?.map((field) => (
                    <FormControlLabel
                      key={field}
                      control={
                        <Checkbox
                          checked={config.fields.includes(field)}
                          onChange={() => handleFieldToggle(field)}
                        />
                      }
                      label={field}
                    />
                  ))}
                </FormGroup>
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setConfig(prev => ({
                      ...prev,
                      fields: availableFields[config.type as keyof typeof availableFields] || []
                    }))}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setConfig(prev => ({ ...prev, fields: [] }))}
                    sx={{ ml: 1 }}
                  >
                    Clear All
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.includeDeleted}
                      onChange={(e) => setConfig(prev => ({ ...prev, includeDeleted: e.target.checked }))}
                    />
                  }
                  label="Include deleted records"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateExport}
              disabled={config.fields.length === 0}
              startIcon={<PlayArrow />}
            >
              Create Export
            </Button>
          </DialogActions>
        </Dialog>

        {/* Job Details Dialog */}
        <Dialog
          open={jobDetailsOpen}
          onClose={() => setJobDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Export Job Details</DialogTitle>
          <DialogContent>
            {selectedJob && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Download />
                      </ListItemIcon>
                      <ListItemText
                        primary="Job ID"
                        secondary={selectedJob.id}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Settings />
                      </ListItemIcon>
                      <ListItemText
                        primary="Data Type"
                        secondary={selectedJob.type.replace('_', ' ').toUpperCase()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FileDownload />
                      </ListItemIcon>
                      <ListItemText
                        primary="Format"
                        secondary={selectedJob.format.toUpperCase()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {getStatusIcon(selectedJob.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary="Status"
                        secondary={
                          <Chip
                            label={selectedJob.status.charAt(0).toUpperCase() + selectedJob.status.slice(1)}
                            color={getStatusColor(selectedJob.status) as any}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Progress"
                        secondary={
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={selectedJob.progress}
                              sx={{ height: 8, borderRadius: 4, mb: 1 }}
                            />
                            <Typography variant="body2">
                              {selectedJob.progress.toFixed(1)}% ({selectedJob.processedRecords.toLocaleString()} / {selectedJob.totalRecords.toLocaleString()} records)
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="File Size"
                        secondary={selectedJob.fileSize ? formatFileSize(selectedJob.fileSize) : 'Not available'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Created At"
                        secondary={new Date(selectedJob.createdAt).toLocaleString()}
                      />
                    </ListItem>
                    {selectedJob.completedAt && (
                      <ListItem>
                        <ListItemText
                          primary="Completed At"
                          secondary={new Date(selectedJob.completedAt).toLocaleString()}
                        />
                      </ListItem>
                    )}
                    {selectedJob.expiresAt && (
                      <ListItem>
                        <ListItemText
                          primary="Expires At"
                          secondary={new Date(selectedJob.expiresAt).toLocaleString()}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                {selectedJob.error && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="subtitle2">Error Details</Typography>
                      <Typography variant="body2">{selectedJob.error}</Typography>
                    </Alert>
                  </Grid>
                )}

                {selectedJob.filters && Object.keys(selectedJob.filters).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Applied Filters</Typography>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <pre>{JSON.stringify(selectedJob.filters, null, 2)}</pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJobDetailsOpen(false)}>
              Close
            </Button>
            {selectedJob?.status === 'completed' && selectedJob.downloadUrl && (
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={() => handleDownload(selectedJob)}
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Export;
